import { Center, SegmentedControl } from '@mantine/core';
import { IconLayoutDashboard, IconTable, IconTestPipe } from '@tabler/icons-react';
import { createFileRoute, retainSearchParams } from '@tanstack/react-router';
import cx from 'clsx';
import dayjs from 'dayjs';
import { motion } from 'motion/react';
import { Activity, Suspense, useDeferredValue, useState } from 'react';
import z from 'zod';

import { LabeledLoader } from '#core/components/layout/LabeledLoader';

import classes from './overview.module.css';
import { MainCanvas } from '#components/3d/MainCanvas';
import { MainScene } from '#components/3d/MainScene';
import { allDataColumns } from '#components/data/constants';
import { DateSlider } from '#components/layout/DateSlider';
import { SceneTitle } from '#components/layout/SceneTitle';
import { Sidebar } from '#components/layout/Sidebar';
import { useFieldAssets } from '#hooks/useFieldAssets';
import type { WellModelOutput } from '#schema/model/WellModel';

// TODO: Separate L/S wells - treat as two different wells
// TODO: Static numbers should always correspond to selected date
// TODO: Improve data table for water analysis - add well filter
// TODO: Handle scrolling in value history table, invert to have latest at top
// TODO: Add filtering by minimum date, filter to hide missing values
// TODO: Improve well links, add arrow/icon to indicate navigation - maybe button appearance
// TODO: L/S distinction in water analysis table

const routeSearchSchema = z.object({
  date: z
    .string()
    .optional()
    .transform((value) => (value !== undefined ? dayjs(value) : undefined)),
  installationType: z.enum(['wellhead', 'island']).optional(),
  scalingFormat: z.enum(['SI', 'SR']).optional(),
  dataOverlay: z.enum(allDataColumns).optional(),
  startDate: z.string().nullable().optional().default(dayjs().subtract(3, 'year').startOf('year').toISOString()),
});

export type RouteSearchSchema = z.infer<typeof routeSearchSchema>;

export const Route = createFileRoute('/dashboard/overview/{-$field}/{-$reservoir}/{-$platform}/{-$well}')({
  component: RouteComponent,
  validateSearch: routeSearchSchema,
  search: {
    middlewares: [
      ({ search, next }) => {
        const result = next(search);
        return {
          ...result,
          date: result.date?.format('YYYY-MM-DD'),
          scalingFormat: result.scalingFormat ?? 'SR',
          dataOverlay: result.dataOverlay ?? 'CALCULATED_SEAWATER_PERCENTAGE',
        };
      },
      retainSearchParams(true),
    ],
  },
  staticData: {
    route: {
      title: 'Overview',
      icon: IconLayoutDashboard,
      sidebarPosition: 1,
    },
  },
});

function RouteComponent() {
  console.debug('Render main route');

  const { assetsByView } = useFieldAssets();

  const selectedField = Route.useParams({
    select: ({ field }) => assetsByView.Field?.find(({ externalId }) => externalId === field),
  });
  const selectedReservoir = Route.useParams({
    select: ({ reservoir }) => assetsByView.Reservoir?.find(({ externalId }) => externalId === reservoir),
  });
  const selectedWell = Route.useParams({
    select: ({ well }) => assetsByView.Well?.find(({ externalId }) => externalId === well),
  });

  const selectedWells: WellModelOutput[] = useDeferredValue(
    (
      selectedReservoir?.children.flatMap((platform) => platform.children) ??
      selectedField?.children?.flatMap((reservoir) => reservoir.children.flatMap((platform) => platform.children)) ??
      assetsByView.Field?.flatMap((field) =>
        field?.children?.flatMap((reservoir) => reservoir.children.flatMap((platform) => platform.children)),
      ) ??
      []
    ).filter((it) => it !== undefined),
  );

  const [sidebarSection, setSidebarSection] = useState('overview');
  const startDate = dayjs().subtract(5, 'y').startOf('month');
  const endDate = dayjs().endOf('month');

  return (
    <main className={classes.main}>
      <div className={classes.canvas}>
        {/* TODO: Scene context */}
        <div className={classes.title}>
          <SceneTitle />
        </div>

        <div className={classes.wells}>
          <Suspense fallback={<LabeledLoader label="Loading 3D model..." />}>
            <MainCanvas>
              <MainScene />
            </MainCanvas>
          </Suspense>
        </div>

        <div className={classes.actions}>
          <SegmentedControl
            fullWidth
            radius="md"
            value={sidebarSection}
            onChange={setSidebarSection}
            data={[
              {
                value: 'overview',
                label: (
                  <Center style={{ gap: 10 }}>
                    <IconTable size={16} />
                    <span>Overview</span>
                  </Center>
                ),
              },
              {
                value: 'water-analysis',
                disabled: selectedWells?.length === 0,
                label: (
                  <Center style={{ gap: 10 }}>
                    <IconTestPipe size={16} />
                    <span>Water Analysis</span>
                  </Center>
                ),
              },
            ]}
          />
        </div>

        <motion.aside layout className={cx(classes.sidebar, sidebarSection === 'water-analysis' && classes.overlay)}>
          <div className={classes.sidebarContent}>
            <Activity>
              <Sidebar
                section={sidebarSection}
                selectedWells={selectedWells}
                fields={assetsByView.Field}
                reservoir={selectedReservoir}
                well={selectedWell}
              />
            </Activity>
          </div>
        </motion.aside>

        <div className={classes.filter}>
          <DateSlider start={startDate} end={endDate} resolution="M" />
        </div>
      </div>
    </main>
  );
}
